import React from 'react';
import { Alert, Space, Divider, Card, Row, Col } from 'antd';
import { CloseCircleOutlined } from '@ant-design/icons';
import { getExcelColumnName } from './constants';

const ErrorDisplay = ({ validationErrors, groupedErrors, errorRows }) => {
	return (
		<>
			<div className="error-validation-wrapper">
				<Alert
					message={
						<Space direction="vertical" size={8} style={{ width: '100%' }}>
							<Space>
								<CloseCircleOutlined style={{ color: '#ff4d4f', fontSize: 20 }} />
								<span style={{ fontSize: 18, fontWeight: 600, color: '#cf1322' }}>
									Validation Failed!
								</span>
							</Space>
							<div style={{ paddingLeft: 28 }}>
								<p style={{ margin: 0, fontSize: 14, color: '#595959' }}>
									Found <strong style={{ color: '#ff4d4f' }}>{validationErrors.length}</strong> error{validationErrors.length > 1 ? 's' : ''} across <strong style={{ color: '#ff4d4f' }}>{errorRows.length}</strong> row{errorRows.length > 1 ? 's' : ''}. Please review and fix the issues below, then upload the corrected file.
								</p>
							</div>
						</Space>
					}
					type="error"
					showIcon={false}
					style={{
						border: '2px solid #ffccc7',
						borderRadius: 12,
						background: 'linear-gradient(135deg, #fff2f0 0%, #ffccc7 100%)',
						boxShadow: '0 4px 12px rgba(255, 77, 79, 0.15)'
					}}
				/>
			</div>

			<Divider orientation="left" style={{ 
				fontSize: 16, 
				fontWeight: 600, 
				marginTop: 32,
				marginBottom: 24,
				borderTopColor: '#ffa39e'
			}}>
				<Space>
					<CloseCircleOutlined style={{ color: '#ff4d4f', fontSize: 18 }} />
					<span style={{ color: '#262626' }}>Validation Errors - Grouped by Row</span>
				</Space>
			</Divider>

			<Space direction="vertical" size={16} style={{ width: '100%' }}>
				{errorRows.map((rowNumber) => {
					const rowErrors = groupedErrors[rowNumber];
					return (
						<Card
							key={rowNumber}
							size="small"
							className="error-row-card"
							style={{
								border: '1px solid #ffccc7',
								backgroundColor: '#fff',
								borderRadius: 8,
								overflow: 'hidden'
							}}
							title={
								<Space style={{ width: '100%', justifyContent: 'space-between' }}>
									<Space>
										<div
											style={{
												background: 'linear-gradient(135deg, #ff4d4f 0%, #cf1322 100%)',
												color: 'white',
												padding: '4px 16px',
												borderRadius: 6,
												fontWeight: 600,
												fontSize: 14,
												boxShadow: '0 2px 8px rgba(255, 77, 79, 0.3)'
											}}>
											Row {rowNumber}
										</div>
										<div style={{ 
											color: '#ff4d4f', 
											fontWeight: 600,
											fontSize: 13,
											background: '#fff1f0',
											padding: '4px 12px',
											borderRadius: 6,
											border: '1px solid #ffccc7'
										}}>
											{rowErrors.length} error{rowErrors.length > 1 ? 's' : ''}
										</div>
									</Space>
								</Space>
							}>
							<Space direction="vertical" size={12} style={{ width: '100%' }}>
								{rowErrors.map((error, idx) => (
									<div
										key={idx}
										className="error-item"
										style={{
											padding: '14px 16px',
											background: 'linear-gradient(135deg, #ffffff 0%, #fafafa 100%)',
											borderLeft: '4px solid #ff4d4f',
											borderRadius: 6,
											boxShadow: '0 2px 6px rgba(0, 0, 0, 0.04)',
											transition: 'all 0.2s ease'
										}}
										onMouseEnter={(e) => {
											e.currentTarget.style.boxShadow = '0 4px 12px rgba(255, 77, 79, 0.12)';
											e.currentTarget.style.transform = 'translateX(4px)';
										}}
										onMouseLeave={(e) => {
											e.currentTarget.style.boxShadow = '0 2px 6px rgba(0, 0, 0, 0.04)';
											e.currentTarget.style.transform = 'translateX(0)';
										}}>
										<Row gutter={[16, 8]}>
											<Col span={7}>
												<div style={{ 
													fontSize: 11, 
													color: '#8c8c8c', 
													marginBottom: 6,
													textTransform: 'uppercase',
													letterSpacing: '0.5px',
													fontWeight: 600
												}}>
													Column
												</div>
												<div style={{ 
													fontWeight: 700, 
													color: '#262626',
													fontSize: 14,
													background: '#f5f5f5',
													padding: '6px 12px',
													borderRadius: 4,
													display: 'inline-block'
												}}>
													{getExcelColumnName(error.column)}
												</div>
											</Col>
											<Col span={7}>
												<div style={{ 
													fontSize: 11, 
													color: '#8c8c8c', 
													marginBottom: 6,
													textTransform: 'uppercase',
													letterSpacing: '0.5px',
													fontWeight: 600
												}}>
													Current Value
												</div>
												<div
													style={{
														color: '#ff4d4f',
														fontFamily: 'Menlo, Monaco, "Courier New", monospace',
														backgroundColor: '#fff1f0',
														padding: '6px 12px',
														borderRadius: 4,
														display: 'inline-block',
														border: '1px solid #ffccc7',
														fontSize: 13,
														fontWeight: 600
													}}>
													{String(error.value || '<empty>')}
												</div>
											</Col>
											<Col span={10}>
												<div style={{ 
													fontSize: 11, 
													color: '#8c8c8c', 
													marginBottom: 6,
													textTransform: 'uppercase',
													letterSpacing: '0.5px',
													fontWeight: 600
												}}>
													Error Description
												</div>
												<div style={{ 
													color: '#ff4d4f', 
													fontWeight: 500,
													fontSize: 13,
													lineHeight: '1.6'
												}}>
													{error.error}
												</div>
											</Col>
										</Row>
									</div>
								))}
							</Space>
						</Card>
					);
				})}
			</Space>

			{errorRows.length > 5 && (
				<Alert
					message="Tip"
					description="Fix errors row by row starting from Row 2. After fixing, save your Excel file and re-upload it here."
					type="info"
					showIcon
					style={{ marginTop: 16 }}
				/>
			)}
		</>
	);
};

export default ErrorDisplay;
